import React, { useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';

// Theme flag shared with Modal.Header/Body/Footer so the light variant can
// flip surfaces the sub-components own.
const ModalThemeContext = React.createContext(false);

// Centred modal — the bigger sibling of ConfirmDialog. Use this when the
// content is rich enough to warrant Header / Body / Footer slots (forms,
// configuration panels, celebratory states) but the interaction is still
// modal in scope. For one-line "are you sure" prompts, prefer
// ConfirmDialog instead.
//
// Usage:
//   <Modal open={open} onClose={handleClose} maxWidth={560} ariaLabel="…">
//     <Modal.Header eyebrow="…" title="…" sub="…" onClose={handleClose} />
//     <Modal.Body> … </Modal.Body>
//     <Modal.Footer> … </Modal.Footer>
//   </Modal>
//
// Behaviour mirrors SideDrawer:
//   - Escape key closes (delegates to onClose so callers can intercept
//     for unsaved-changes prompts)
//   - Backdrop click closes via the same onClose
//   - Focus moves into the modal on open and traps inside it while open
//   - Body scroll is locked while open
//   - Mounted via createPortal to document.body
//
// Top accent stripe — the 30° brand chevron — is on by default and can be
// turned off via stripe={false} for celebratory variants that paint their
// own header treatment.
export default function Modal({
  open,
  onClose,
  maxWidth = 560,
  ariaLabel,
  stripe = true,
  // Light is the default now (the whole app is light-themed). The
  // Header/Body sub-components are already light-tokened, so a dark shell
  // rendered light-on-dark — every admin caller that didn't pass `light`
  // (compliance modals, AdminTags, Upload/ManageCategories, Reopen,
  // SupplierDetail deactivate) was effectively broken. Opt back into dark
  // with light={false} if a specific modal ever needs it.
  light = true,
  // When true the panel shrinks to its content width (still capped at
  // maxWidth) instead of always filling to maxWidth. Used by the image
  // viewer so the modal hugs the image rather than boxing a small image in a
  // large panel.
  fitContent = false,
  children,
}) {
  const modalRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Snapshot the active element on open so we can restore focus when the
  // modal unmounts. Without this, focus tends to drop to <body> after
  // close and screen readers lose the page anchor.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;
    return () => {
      const el = previouslyFocusedRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  // Move focus into the modal once mounted. requestAnimationFrame gives
  // the fade-in a tick so the focus ring isn't visible mid-transition.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      const el = modalRef.current;
      if (!el) return;
      const focusable = el.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
      else el.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Lock body scroll. Modals that don't lock cause the page to jump when
  // opened on devices with momentum scrolling.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Escape + Tab focus trap. Tab cycles focus within the modal's
  // focusable descendants (matches the SideDrawer trap implementation).
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const el = modalRef.current;
      if (!el) return;
      const focusables = Array.from(el.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(n => n.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <ModalThemeContext.Provider value={light}>
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 150,
        background: light ? 'rgba(30,33,25,0.55)' : 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, overflowY: 'auto',
      }}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || 'Dialog'}
        tabIndex={-1}
        style={{
          background: light ? '#ffffff' : '#1a1a1a',
          border: light ? '1px solid #E6EADB' : '1px solid #2a2a2a',
          borderRadius: light ? 16 : 10,
          boxShadow: light ? '0 1px 2px rgba(45,48,35,0.04), 0 24px 48px -16px rgba(45,48,35,0.28)' : undefined,
          width: fitContent ? 'fit-content' : '100%',
          maxWidth,
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: 'Manrope, sans-serif',
        }}>
        {stripe && (
          <div style={{
            height: light ? 4 : 3,
            background: light ? '#92D400' : 'linear-gradient(90deg, #92D400 0%, #92D400 30%, transparent 30%)',
            flexShrink: 0,
          }} />
        )}
        {children}
      </div>
    </div>
    </ModalThemeContext.Provider>,
    document.body
  );
}

function Header({ eyebrow, title, sub, onClose }) {
  return (
    <div className="px-7 pt-5 pb-4 border-b border-line flex items-start justify-between gap-4 flex-shrink-0">
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-1.5">
            {eyebrow}
          </div>
        )}
        <div className="font-['Metropolis'] text-lg font-bold text-ink mb-1 truncate">{title}</div>
        {sub && <div className="font-['Metropolis'] text-xs text-[#51534A]">{sub}</div>}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center bg-transparent border border-line rounded text-[#51534A] hover:text-ink hover:border-[#DADCCD] transition-colors flex-shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

function Body({ children }) {
  return (
    <div className="flex-1 overflow-y-auto px-7 py-6">
      {children}
    </div>
  );
}

function Footer({ children }) {
  const light = useContext(ModalThemeContext);
  return (
    <div className={`px-7 py-3.5 border-t border-line flex-shrink-0 ${light ? 'bg-[#F7F9F0]' : 'bg-black/20'}`}>
      {children}
    </div>
  );
}

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
